import { useMutation, useQuery } from '@apollo/client';
import { Box, Card, CardContent, TextField } from '@mui/material';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router';
import { createUser, doesUserExist, getUserByID } from 'src/api/users';
import { useAlert } from 'src/components/alert';
import Breadcrumbs from 'src/components/custom-breadcrumbs';
import { useSnackbar } from 'src/components/snackbar';
import { GET_ORGANIZATION_BY_ID } from 'src/graphql/organizations/queries';
import { GET_ROLES } from 'src/graphql/roles/queries';
import { UPSERT_TASK_ASSIGNMENTS } from 'src/graphql/tasks/queries';
import { useAddOrUnarchiveOrgUser } from 'src/hooks/use-add-or-unarchive-org-user';
import { useRouter } from 'src/routes/hook/use-router';
import Button from '../../components/Button';
import DataForm from './components/org-users/data-form';

const OrgUsersCreate = ({ inCampaigns, organizationId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const alert = useAlert();
  let { orgId } = useParams();
  const { data: roleData } = useQuery(GET_ROLES);
  const { data: orgData } = useQuery(GET_ORGANIZATION_BY_ID, {
    variables: {
      org_id: orgId,
    },
  });
  const [addOrgUser] = useAddOrUnarchiveOrgUser();
  const [upsertTaskAssignments] = useMutation(UPSERT_TASK_ASSIGNMENTS);

  const [showUserForm, setShowUserForm] = useState(false);
  const [existingUser, setExistingUser] = useState();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const { tasks, org_role, user_role, ...values } = data;
      console.log(tasks);
      const user = existingUser?.user_id
        ? existingUser
        : await createUser({
            ...values,
            role: user_role,
          });
      await addOrgUser({
        variables: {
          data: {
            organization_id: orgId,
            user_id: user.user_id,
            role_id: roleData.rows.find((r) => r.code === org_role).role_id,
          },
        },
      });
      if (tasks?.length) {
        await upsertTaskAssignments({
          variables: {
            data: tasks.map((task) => ({
              task_id: task.value,
              user_id: user?.user_id,
            })),
          },
        });
      }
      if (existingUser?.user_id) {
        alert.dialog({
          severity: 'success',
          title: 'User added!',
          description: 'Existing user was added to the organization.',
          confirmationText: 'Close',
          showCancelButton: false,
          onConfirm: () => {
            router.back();
          },
        });
      } else {
        alert.dialog({
          severity: 'success',
          title: 'New User created!',
          description: 'An email will be sent with login information.',
          confirmationText: 'Close',
          showCancelButton: false,
          onConfirm: () => {
            router.back();
          },
        });
      }
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const searchUser = async () => {
    const existingUserId = await doesUserExist(email);

    if (existingUserId) {
      const user = await getUserByID(existingUserId);

      if (user) {
        setExistingUser({ ...user });
      }
      setShowUserForm(true);
    } else {
      setExistingUser(null);
      setShowUserForm(true);
    }
  };

  return (
    <Container>
      <Helmet title="Add Organization Admin" />
      <Breadcrumbs
        heading="Add Organization Admin"
        links={
          inCampaigns
            ? [
                {
                  name: 'Settings',
                },
                {
                  name: 'Org Admins',
                  href: '/dashboard/users/',
                },
                {
                  name: 'Add Org Admin',
                },
              ]
            : [
                { name: 'Organizations', href: '/superadmin/organizations' },
                {
                  name: orgData?.row?.name,
                  href: `/superadmin/organizations/${orgId}/users`,
                },
                { name: 'New User' },
              ]
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {/* <Alert severity="warning">
            Adding an organization admin will grant them access to all campaigns and tasks under
            your organization
          </Alert> */}
        </Grid>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="Email"
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                />
                <Button sx={{ ml: 2 }} onClick={searchUser} disabled={!email}>
                  {'Search'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {showUserForm && (
        <Grid container spacing={3}>
          <Grid item md={8}>
            <DataForm
              loading={loading}
              inCampaigns={inCampaigns}
              email={email}
              onSubmit={onSubmit}
              user={existingUser}
              disableFields={!!existingUser}
            />
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default OrgUsersCreate;
